/**
 * Hook del listado de Tarifas con paginación server-side (US-2.4).
 *
 * Espejo de `useLocaciones`/`useAllies`: filtros (`type`/`validAt`) + `offset`/`limit`
 * + `meta` del backend, con loading/error/refresh. La red vive en `tariffApi`; aquí
 * solo el estado de la vista (separable de la presentación).
 */
import { useCallback, useEffect, useState } from 'react';
import type { ListTariffsQuery, PageMeta, Tariff } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { tariffApi, type TariffListParams } from '../api/tariffApi';

/** Límite por página por defecto del Admin Web. */
export const DEFAULT_TARIFFS_LIMIT = 20;

/** Filtros del listado (los del contrato, sin paginación). */
export type TariffFilters = Omit<ListTariffsQuery, 'offset' | 'limit'>;

export interface UseTariffsOptions {
  /** Elementos por página (default `DEFAULT_TARIFFS_LIMIT`). */
  limit?: number;
  /** Filtros iniciales. */
  initialFilters?: TariffFilters;
}

export interface UseTariffsResult {
  data: Tariff[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  /** Filtros activos. */
  filters: TariffFilters;
  /** Desplazamiento actual (paginación server-side). */
  offset: number;
  limit: number;
  /** Aplica filtros nuevos y reinicia la paginación a la primera página. */
  setFilters: (filters: TariffFilters) => void;
  /** Mueve la página (offset) actual. */
  setOffset: (offset: number) => void;
  /** Recarga la página actual (p. ej. tras crear/editar/eliminar una tarifa). */
  refresh: () => void;
}

const emptyMeta = (limit: number): PageMeta => ({ offset: 0, limit, total: 0, hasMore: false });

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar las tarifas.';
}

export function useTariffs(options: UseTariffsOptions = {}): UseTariffsResult {
  const limit = options.limit ?? DEFAULT_TARIFFS_LIMIT;
  const [filters, setFiltersState] = useState<TariffFilters>(() => options.initialFilters ?? {});
  const [offset, setOffsetState] = useState(0);
  const [data, setData] = useState<Tariff[]>([]);
  const [meta, setMeta] = useState<PageMeta>(() => emptyMeta(limit));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: TariffListParams = { ...filters, offset, limit };
    tariffApi
      .list(params)
      .then((page) => {
        if (cancelled) return;
        setData(page.data);
        setMeta(page.meta);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Descarta respuestas de una página/filtro anterior que lleguen fuera de orden.
    return () => {
      cancelled = true;
    };
  }, [filters, offset, limit, reloadKey]);

  const setFilters = useCallback((next: TariffFilters) => {
    setOffsetState(0);
    setFiltersState(next);
  }, []);

  const setOffset = useCallback((next: number) => {
    setOffsetState(next < 0 ? 0 : next);
  }, []);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { data, meta, loading, error, filters, offset, limit, setFilters, setOffset, refresh };
}
